import React, { useState, useRef, useEffect, useCallback } from "react";
import { Checkbox } from "@material-ui/core";
import Icon from "../../../utils/icons";
import { formatScenes,formatSchedulerTime, generateHourArray, formatTime, formatHHMM, snap, getDefaultSchedulerData, frameSchedulerData, frameSchedulerDataForSaving } from './utils/helpers'
import { HOURS, ROW_HEIGHT, DAYS, BLOCK_GAP, SCENES } from '../../../utils/constant'
import Tooltip from './utils/Tooltip'
import I18n from "../../../localization";
import Dropdown from './utils/dropdown'
import { useMobileStyles } from "./styles";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableBlockCard({ block, index, mode, selectedBlockIndex, setSelectedBlockIndex, deleteSingleScene, classes }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isReorderMode = mode === 'reorder';

  return (
    <div
      ref={isReorderMode ? setNodeRef : undefined}
      style={isReorderMode ? style : undefined}
      className={`${classes.editBlockCard} 
        ${isDragging ? classes.dragging : ""}
        ${isReorderMode ? classes.reorderMode : ""}
        ${selectedBlockIndex === index ? (isReorderMode ? classes.reorderActiveCard : classes.activeCard) : ''}
      `}
      {...(isReorderMode ? { ...attributes, ...listeners } : {})}
      onClick={() => {
        if (isDragging) return;
        if (mode === 'delete') return;
        if (isReorderMode) {
          setSelectedBlockIndex(index);
          return;
        }
        setSelectedBlockIndex((prev) =>
          prev === index ? null : index
        );
      }}
    >
      {mode === 'delete' && (
        <div
          className={classes.deleteBadge}
          onClick={(e) => {
            e.stopPropagation();
            deleteSingleScene(index);
          }}
        >
          −
        </div>
      )}
      <div
        className={classes.editBlockColor}
        style={{ background: block.color }}
      />
      <div className={classes.editBlockName}>{block.name}</div>
    </div>
  );
}

export default function IQAirScheduler({
  scheduleData = null,
  isDarkMode = false,
  presets = [],
  isReadOnlyMode = false,
  onScheduleDataChange,
  showPopupHeader = false,
  popupTitle = I18n.t("mobile.iqAirScheduler.editSchedule"),
  onClosePopup = null,
  handleClick
}) {
  const classes = useMobileStyles();
  const timelineRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [sourceDay, setSourceDay] = useState(null);
  const [mode, setMode] = useState(null); // "add" | "delete" | "reorder" | null
  const [selectedDays, setSelectedDays] = useState([]);
  const [loader, setLoader] = useState(false);
  const [savingLoader, setSavingLoader] = useState(false);
  const [data, setData] = useState(scheduleData ?? null);
  const [selectedBlockIndex, setSelectedBlockIndex] = useState(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [showStartDropdown, setShowStartDropdown] = useState(false);
  const [showEndDropdown, setShowEndDropdown] = useState(false);
  const plusRef = useRef(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const editBlocksRef = useRef(null);

  const handlePopupClose = () => {
    if (typeof onClosePopup === "function") {
      onClosePopup();
      return;
    }
    if (typeof handleClick === "function") {
      handleClick();
    }
  };

  const hasValidSetpoint = (value) => {
    if (value === null || value === undefined) return false;
    const numericValue = Number(value);
    return !Number.isNaN(numericValue) && numericValue !== 0;
  };

  const isOffTemperatureMode = (mode) => String(mode || '').toUpperCase() === 'OFF';

  const dropdownPresets = presets.filter((preset) => {
    if (isOffTemperatureMode(preset?.['temperature-mode'])) {
      return true;
    }

    return (
      hasValidSetpoint(preset?.['cool-setpoint']) ||
      hasValidSetpoint(preset?.['heat-setpoint'])
    );
  });


  // ✅ Tooltip state
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    text: "",
    dayIdx: null
  });




  // @dnd-kit sensors for reorder mode
  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 5 } });
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } });
  const sensors = useSensors(pointerSensor, touchSensor);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || sourceDay === null) return;

    setData((prev) => {
      if (!prev?.[sourceDay]) return prev;
      const blocks = [...prev[sourceDay]];
      const oldIndex = blocks.findIndex((b) => b.id === active.id);
      const newIndex = blocks.findIndex((b) => b.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;

      const reordered = arrayMove(blocks, oldIndex, newIndex);

      // Update selectedBlockIndex to follow the moved card
      setSelectedBlockIndex(newIndex);

      return {
        ...prev,
        [sourceDay]: normalizeBlocks(reordered)
      };
    });
  }, [sourceDay]);

  useEffect(() => {
    if (isReadOnlyMode || typeof onScheduleDataChange !== "function") {
      setData(scheduleData ?? null);
    }
  }, [scheduleData, isReadOnlyMode, onScheduleDataChange]);

  useEffect(() => {
    if (typeof onScheduleDataChange === "function" && data) {
      onScheduleDataChange(data);
    }
  }, [data, onScheduleDataChange]);

  useEffect(() => {
    if (mode === 'reorder') {
      setSelectedBlockIndex(0);
    } else {
      setSelectedBlockIndex(null);
    }
  }, [mode])

  useEffect(() => {
    const blockCount = data?.[sourceDay]?.length || 0;
    if (blockCount === 1 && (mode === 'delete' || mode === 'reorder')) {
      setMode(null);
    }
  }, [data, sourceDay, mode]);

  useEffect(() => {

    if (!loader && data) {
      const updateWidth = () => {
        if (timelineRef.current) {
          setContainerWidth(timelineRef.current.offsetWidth);
        }
      };
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
  }, [loader, data]);

  useEffect(() => {
    if (data == null) return;
    if (sourceDay === null) { setMode(null); setSelectedBlockIndex(null); return };
    if (!selectedDays.length) return;

    setData((prev) => {
      if (prev == null || !prev?.[sourceDay]) return prev;
      const updated = { ...prev };

      selectedDays.forEach((day) => {
        if (day === sourceDay) return;

        updated[day] = prev[sourceDay].map((b) => ({
          ...b,
          id: `${b.name}_${Math.random()}`
        }));
      });

      return updated;
    });

  }, [selectedDays, sourceDay, data?.[sourceDay]]);

  const PX_PER_HOUR = containerWidth / HOURS;
  const timeToPx = (h) => h * PX_PER_HOUR;

  const updateBlockTime = (type, value) => {
    if (selectedBlockIndex === null || sourceDay === null) return;

    const MIN_DURATION = 0.5;
    const roundToHalfHour = (val) => Math.round(val * 2) / 2;

    setData((prev) => {
      const updated = { ...prev };

      selectedDays.forEach((dayIdx) => {
        let blocks = [...(updated[dayIdx] || [])];

        const original = blocks.map((b) => ({ ...b }));
        const block = original[selectedBlockIndex];
        if (!block) return;

        const requestedValue = roundToHalfHour(value);

        if (type === "end") {
          if (selectedBlockIndex === blocks.length - 1) {
            blocks[selectedBlockIndex] = { ...blocks[selectedBlockIndex], end: 24 };
            updated[dayIdx] = blocks;
            return;
          }

          const trailingCount = blocks.length - selectedBlockIndex - 1;
          const minEnd = roundToHalfHour(block.start + MIN_DURATION);
          const maxEnd = roundToHalfHour(24 - trailingCount * MIN_DURATION);
          const newEnd = Math.max(minEnd, Math.min(requestedValue, maxEnd));

          blocks[selectedBlockIndex] = {
            ...blocks[selectedBlockIndex],
            end: newEnd
          };

          const oldEnd = block.end;

          if (newEnd <= oldEnd) {
            blocks[selectedBlockIndex + 1] = {
              ...blocks[selectedBlockIndex + 1],
              start: newEnd
            };
            updated[dayIdx] = blocks;
            return;
          }

          let carry = roundToHalfHour(newEnd - oldEnd);

          for (let k = selectedBlockIndex + 1; k < blocks.length; k++) {
            const oldStart = original[k].start;
            const oldEndK = original[k].end;
            const oldDuration = roundToHalfHour(oldEndK - oldStart);

            if (carry <= 0) {
              if (k === selectedBlockIndex + 1) {
                blocks[k] = { ...blocks[k], start: newEnd };
              }
              break;
            }

            const shrinkable = roundToHalfHour(oldDuration - MIN_DURATION);

            if (carry <= shrinkable) {
              blocks[k] = {
                ...blocks[k],
                start: roundToHalfHour(oldStart + carry),
                end: oldEndK
              };
              carry = 0;
              break;
            }

            const remaining = roundToHalfHour(carry - shrinkable);
            blocks[k] = {
              ...blocks[k],
              start: roundToHalfHour(oldEndK - MIN_DURATION + remaining),
              end: roundToHalfHour(oldEndK + remaining)
            };
            carry = remaining;
          }

          blocks[0] = { ...blocks[0], start: 0 };
          const lastIdx = blocks.length - 1;
          blocks[lastIdx] = { ...blocks[lastIdx], end: 24 };
        }

        if (type === "start") {
          if (selectedBlockIndex === 0) {
            blocks[selectedBlockIndex] = { ...blocks[selectedBlockIndex], start: 0 };
            updated[dayIdx] = blocks;
            return;
          }

          const leadingCount = selectedBlockIndex;
          const minStart = roundToHalfHour(leadingCount * MIN_DURATION);
          const maxStart = roundToHalfHour(block.end - MIN_DURATION);
          const newStart = Math.max(minStart, Math.min(requestedValue, maxStart));

          blocks[selectedBlockIndex] = {
            ...blocks[selectedBlockIndex],
            start: newStart
          };

          const oldStart = block.start;

          if (newStart >= oldStart) {
            blocks[selectedBlockIndex - 1] = {
              ...blocks[selectedBlockIndex - 1],
              end: newStart
            };
            updated[dayIdx] = blocks;
            return;
          }

          let carry = roundToHalfHour(oldStart - newStart);

          for (let k = selectedBlockIndex - 1; k >= 0; k--) {
            const oldStartK = original[k].start;
            const oldEndK = original[k].end;
            const oldDuration = roundToHalfHour(oldEndK - oldStartK);

            if (carry <= 0) {
              if (k === selectedBlockIndex - 1) {
                blocks[k] = { ...blocks[k], end: newStart };
              }
              break;
            }

            const shrinkable = roundToHalfHour(oldDuration - MIN_DURATION);

            if (carry <= shrinkable) {
              blocks[k] = {
                ...blocks[k],
                start: oldStartK,
                end: roundToHalfHour(oldEndK - carry)
              };
              carry = 0;
              break;
            }

            const remaining = roundToHalfHour(carry - shrinkable);
            blocks[k] = {
              ...blocks[k],
              start: roundToHalfHour(oldStartK - remaining),
              end: roundToHalfHour(oldStartK + MIN_DURATION - remaining)
            };
            carry = remaining;
          }

          blocks[0] = { ...blocks[0], start: 0 };
          const lastIdx = blocks.length - 1;
          blocks[lastIdx] = { ...blocks[lastIdx], end: 24 };
        }

        updated[dayIdx] = blocks;
      });

      return updated;
    });
  };



  const addSceneBlock = (scene) => {

    const dayIdx = sourceDay;

    setData((prev) => {
      const blocks = [...(prev[dayIdx] || [])];

      if (blocks.length >= 6) return prev;
      const BLOCK_SIZE = 2;
      // 🔍 find block to shrink
      let shrinkIndex = -1;

      for (let i = blocks.length - 1; i >= 0; i--) {
        if (blocks[i].end - blocks[i].start > BLOCK_SIZE) {
          shrinkIndex = i;
          break;
        }
      }

      if (shrinkIndex === -1) return prev;

      // 👉 ALWAYS use last block for placement
      const lastBlock = blocks[blocks.length - 1];
      const newEnd = lastBlock.end;
      const newStart = newEnd - BLOCK_SIZE;

      // 🔹 shrink chosen block
      blocks[shrinkIndex] = {
        ...blocks[shrinkIndex],
        end: blocks[shrinkIndex].end - BLOCK_SIZE
      };

      // 🔹 shift all blocks after shrinkIndex
      for (let i = shrinkIndex + 1; i < blocks.length; i++) {
        blocks[i] = {
          ...blocks[i],
          start: blocks[i].start - BLOCK_SIZE,
          end: blocks[i].end - BLOCK_SIZE
        };
      }

      // 🔹 add new block at END
      const newBlock = {
        id: `${scene.name.toLowerCase()}_${Date.now()}_${Math.random()}`,
        presetId: scene.presetId,
        start: newStart,
        end: newEnd,
        color: scene.color,
        name: scene.name
      };

      blocks.push(newBlock);

      return {
        ...prev,
        [dayIdx]: blocks
      };
    });
  };



  const normalizeBlocks = (blocks) => {
    let currentStart = 0;

    return blocks.map((b) => {
      const duration = b.end - b.start;

      const newBlock = {
        ...b,
        start: currentStart,
        end: currentStart + duration
      };

      currentStart += duration;

      return newBlock;
    });
  };

  const deleteSingleScene = (blockIndex) => {
    setData((prev) => {
      const updated = { ...prev };

      selectedDays.forEach((dayIdx) => {
        let blocks = [...(updated[dayIdx] || [])];

        if (blockIndex < 0 || blockIndex >= blocks.length) return;
        if (blocks.length === 1) return;

        const removed = blocks[blockIndex];

        // 👉 merge logic
        if (blockIndex > 0) {
          blocks[blockIndex - 1] = {
            ...blocks[blockIndex - 1],
            end: removed.end
          };
        } else {
          blocks[1] = {
            ...blocks[1],
            start: removed.start
          };
        }

        blocks.splice(blockIndex, 1);

        updated[dayIdx] = blocks;
      });

      return updated;
    });
    setSelectedBlockIndex(null);
  };

  if (data == null) {
    return null
  }

  const sourceBlocks = Array.isArray(data?.[sourceDay])
    ? data[sourceDay]
    : Array.isArray(data?.[0])
      ? data[0]
      : [];
  const sourceBlockCount = sourceBlocks.length;
  const selectedSourceBlock = Number.isInteger(selectedBlockIndex) && selectedBlockIndex >= 0
    ? sourceBlocks[selectedBlockIndex]
    : null;
  const selectedStartTime = selectedSourceBlock?.start ?? 0;
  const selectedEndTime = selectedSourceBlock?.end ?? 0;

  return (
    <div className={`${classes.schedulerWrapper} ${isReadOnlyMode ? classes.isReadOnlyMode : ''}`} >

      {loader ? <div className={classes.loaderContainer}>
        <Icon src="loaderV4" width="60px" height="60px" />
      </div> :
        <>
          {showPopupHeader && (
            <div className={classes.popupHeader}>
              <div className={classes.popupTitle}>{popupTitle}</div>
              <button
                type="button"
                className={classes.closeButton}
                onClick={handlePopupClose}
              >
                <Icon src="close" />
              </button>
            </div>
          )}
        <div className={classes.wrapper}>
            <div className={classes.pageTitle}>{I18n.t("mobile.iqAirScheduler.selectDayToEdit")}</div>
            {DAYS.map((d, dayIdx) => {
              const isSource = sourceDay === dayIdx;
              const isSelected = selectedDays.includes(dayIdx);

              return (
                <div key={dayIdx} className={classes.row}
                  onClick={() => {
                    setSelectedDays((prev) => {
                      let updated;

                      if (prev.includes(dayIdx)) {
                        // ❌ deselect
                        updated = prev.filter((d) => d !== dayIdx);
                      } else {
                        // ✅ select
                        updated = [...prev, dayIdx];
                      }

                      // ✅ set source day logic
                      if (!updated.length) {
                        setSourceDay(null);
                      } else if (!sourceDay || !updated.includes(sourceDay)) {
                        setSourceDay(updated[0]); // first selected = source
                      }

                      return updated;
                    });
                  }}
                >
                  <div
                    className={classes.dayLabel}
                    style={{
                      background: isSelected
                        ? "#01B5DE"     // targets
                        :isDarkMode ? "#262627": isReadOnlyMode ? "#fff" : "#e0e0e0",
                      color: isSource || isSelected ? "#f8f8ff" : isDarkMode ? "#fff":"#545456"
                    }}
                  >
                    {d}
                  </div>

                  <div className={classes.timeline} ref={dayIdx === 0 ? timelineRef : null}>
                    {[...Array(HOURS + 1)].map((_, i) => (
                      <div
                        key={i}
                        className={classes.hourLine}
                        style={{
                          left: timeToPx(i),
                          opacity:isReadOnlyMode ? 0.3:1
                        }}
                      />
                    ))}

                    {(Array.isArray(data?.[dayIdx]) ? data[dayIdx] : []).map((b, i, arr) => {
                      const isFirst = i === 0;
                      const isLast = i === arr.length - 1;

                      const gapLeft = isFirst ? 0 : BLOCK_GAP / 2;
                      const gapRight = isLast ? 0 : BLOCK_GAP / 2;
                      const hasSelection = selectedDays.length > 0
                      return (
                        <div
                          key={b.id}
                          className={`${classes.timelineBox} ${selectedBlockIndex === i && selectedDays.includes(dayIdx) ? classes.highlightBlock : ""
                            }`}
                          style={{

                            left: timeToPx(b.start) + gapLeft,
                            width: Math.max(
                              0,
                              timeToPx(b.end - b.start) - (gapLeft + gapRight)
                            ),

                            height: (isReadOnlyMode ? 20 : ROW_HEIGHT) - 10,
                            background: b.color,

                            opacity: !hasSelection || isSelected ? 1 : 0.4,

                          }}
                        />
                      );
                    })}

                    {/* {(data[dayIdx] || []).map((b, i, arr) => {
                      if (i === arr.length - 1 || dayIdx !== sourceDay) return null;

                      return (
                        <div
                          key={`handle-${b.id}`}
                          className={classes.handleWrapper}
                          style={{ left: timeToPx(b.end), opacity: isSelected ? 1 : 0.3 }}
                          onMouseDown={(e) =>
                            isSelected && handleResizeStart(e, dayIdx, i)
                          }
                          onTouchStart={(e) =>
                            isSelected && handleResizeStart(e, dayIdx, i)
                          }
                        >
                          <Icon src='schedulerHandle'>  </Icon>
                        </div>
                      );
                    })} */}

                    {tooltip.visible && tooltip.dayIdx === dayIdx && < Tooltip tooltip={tooltip} containerWidth={containerWidth} classes={classes} />}
                  </div>
                </div>
              );
            })}

            <div className={classes.footerRow}>
              <div style={{ width: isReadOnlyMode?16:20 }} />
              <div className={classes.footerTimeline}>
                {(isReadOnlyMode?[ 0,  12,  24] :[0, 4, 8, 12, 16, 20, 24] ).map((t) => (
                  <div
                    key={t}
                    style={{
                      position: "absolute",
                      left: timeToPx(t),
                      transform: "translateX(-50%)"
                    }}
                  >
                    {(() => {

                      return (
                        <div className={classes.xAxisLabel} >
                          <div>{t}</div>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
            <div className={`${selectedDays.length === 0 ? classes.noDaySelected : ''} ${classes.editWrapper}`}>
              <div className={classes.editTimeBox}>
              <div className={`${classes.pageTitle} ${classes.pageTitleEdit}`}>{I18n.t("mobile.iqAirScheduler.selectBlockToEdit")}</div>
                <div className={classes.iconsBox}>

                  {/* ADD */}
                  <div
                    ref={plusRef}
                    className={`${mode === "add" ? classes.selectedModeIcon : ""} ${sourceBlockCount >= 6 ? classes.disableIcon : ""} ${classes.editTimeIcons}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (sourceBlockCount >= 6) return
                      setMode((prev) => (prev === "add" ? null : "add"));
                      setShowAddDropdown((prev) => !prev);
                    }}
                  >
                    <Icon src="plus" />
                  </div>


                  {/* DELETE */}
                  <div
                    className={`${mode === "delete" && sourceBlockCount !== 1 ? classes.selectedModeIcon : ""} ${sourceBlockCount === 1 ? classes.disableIcon : ""} ${classes.editTimeIcons}`}
                    onClick={() => {
                      if (sourceBlockCount === 1) return;
                      setMode((prev) => (prev === "delete" ? null : "delete"))
                    }}
                  >
                    <Icon src="bin" />
                  </div>

                  {/* REORDER */}
                  <div
                    className={`${mode === "reorder" && sourceBlockCount !== 1 ? classes.selectedModeIcon : ""} ${sourceBlockCount === 1 ? classes.disableIcon : ""} ${classes.editTimeIcons}`}
                    onClick={() => {
                      if (sourceBlockCount === 1) return;
                      setMode((prev) => (prev === "reorder" ? null : "reorder"))
                    }}
                  >
                    <Icon src="reorder" />
                  </div>

                </div>
                {showAddDropdown && mode === "add" && (
                  <Dropdown
                    classes={classes}
                    anchorRef={plusRef}
                    onClose={() => { setShowAddDropdown(false); setMode(null) }}
                    onSelect={(scene) => { addSceneBlock(scene); setMode(null) }}
                    data={formatScenes(dropdownPresets,isDarkMode)}
                  />
                )}
              </div>

              {/* ✅ BLOCK CARDS */}
              {mode === 'reorder' ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={sourceBlocks.map(b => b.id)} strategy={horizontalListSortingStrategy}>
                    <div
                      ref={editBlocksRef}
                      className={`${classes.editBlocksRow} ${sourceBlockCount === 1 && mode !== 'add' ? classes.disableEditBlock : ""}`}
                    >
                      {sourceBlocks.map((block, index) => (
                        <SortableBlockCard
                          key={block.id}
                          block={block}
                          index={index}
                          mode={mode}
                          selectedBlockIndex={selectedBlockIndex}
                          setSelectedBlockIndex={setSelectedBlockIndex}
                          deleteSingleScene={deleteSingleScene}
                          classes={classes}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div
                  ref={editBlocksRef}
                  className={`${classes.editBlocksRow} ${mode === 'delete' ? classes.deleteEditRow : ''} ${sourceBlockCount === 1 && mode !== 'add' ? classes.disableEditBlock : ""}`}
                >
                  {sourceBlocks.map((block, index) => (
                    <SortableBlockCard
                      key={block.id}
                      block={block}
                      index={index}
                      mode={mode}
                      selectedBlockIndex={selectedBlockIndex}
                      setSelectedBlockIndex={setSelectedBlockIndex}
                      deleteSingleScene={deleteSingleScene}
                      classes={classes}
                    />
                  ))}
                </div>
              )}

              <div className={`${classes.editTimeTimers} ${mode === 'delete' ? classes.inDeleteMode : ''} ${selectedBlockIndex !== null ? '' : classes.noDaySelected}`} style={{visibility: selectedBlockIndex !== null ? 'visible' : 'hidden'}}>
                <div ref={startDateRef} className={classes.timersField}>
                  <div className={classes.timersFieldLabel}>{I18n.t("mobile.iqAirScheduler.from")}</div>
                  <span onClick={() => setShowStartDropdown(true)}>{formatHHMM(selectedStartTime)}</span>
                  {showStartDropdown && <Dropdown
                    classes={classes}
                    classname='timePicker'
                    anchorRef={startDateRef}
                    onClose={() => { setShowStartDropdown(false); }}
                    onSelect={(val) => {
                      setShowStartDropdown(false);
                      updateBlockTime("start", formatSchedulerTime(val));

                    }}
                    data={generateHourArray()}
                    selectedValue={selectedSourceBlock?.start}
                  />}
                </div>


                <div ref={endDateRef} className={classes.timersField}>
                  <div className={classes.timersFieldLabel}>{I18n.t("mobile.iqAirScheduler.to")}</div>
                  <span onClick={() => setShowEndDropdown(true)}>{formatHHMM(selectedEndTime)}</span>
                  {showEndDropdown && <Dropdown
                    classes={classes}
                    classname='timePicker'
                    anchorRef={endDateRef}
                    onClose={() => { setShowEndDropdown(false); }}
                    onSelect={(val) => {
                      setShowEndDropdown(false);
                      updateBlockTime("end", formatSchedulerTime(val));

                    }}
                    data={generateHourArray()}
                    selectedValue={selectedSourceBlock?.end}
                  />}
                </div>
              </div>

            </div>
            <div style={{visibility: selectedBlockIndex !== null ? 'visible' : 'hidden'}} className={`${classes.note} ${mode === 'delete' ? classes.inDeleteModeVisibility : ''} ${selectedBlockIndex !== null ? '' : classes.noDaySelected}`}>{I18n.t("mobile.iqAirScheduler.timeIntervalNote")}</div>
          </div>
      </>
      }
    </div>
  );
}